
import cn from 'classnames';
import { Link } from 'react-router';
import React, { useState, useRef, useCallback, Suspense, lazy, useMemo } from 'react';
import { getHighestRiskLevel, normalizeRiskLevel } from '../constants/project';
import { HiOutlineBadgeCheck } from 'react-icons/hi';
import { BsClock } from 'react-icons/bs';

// Lazy loading of Mapbox components
const Map = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Map })));
const Marker = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Marker })));
const Source = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Source })));
const Layer = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Layer })));
const NavigationControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.NavigationControl })));
const FullscreenControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.FullscreenControl })));
const GeolocateControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.GeolocateControl })));

// Mapbox Error Boundary Component
const MapboxErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      if (error.message.includes('mapbox') || error.message.includes('Failed to fetch dynamically imported module')) {
        console.error('❌ Mapbox loading error:', error);
        setHasError(true);
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-2">Map Loading Error</div>
          <div className="text-gray-400 text-sm">Please refresh the page to try again</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Lazy loading of CSS with error handling
const loadMapboxCSS = () => {
  try {
    if (!document.querySelector('link[href*="mapbox-gl.css"]')) {
      const link = document.createElement('link');
      link.href = 'https://api.tiles.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      link.onerror = () => {
        console.error('❌ Failed to load Mapbox CSS');
      };
      link.onload = () => {
        console.log('📦 Mapbox CSS loaded');
      };
      document.head.appendChild(link);
    }
    
    // Add custom styles for map controls
    if (!document.querySelector('#mapbox-controls-styles')) {
      const style = document.createElement('style');
      style.id = 'mapbox-controls-styles';
      style.textContent = `
        .mapboxgl-ctrl-group {
          z-index: 1000 !important;
          pointer-events: auto !important;
          background-color: rgba(255, 255, 255, 0.9) !important;
          border: 1px solid rgba(0, 0, 0, 0.1) !important;
        }
        .mapboxgl-ctrl-group button {
          pointer-events: auto !important;
          cursor: pointer !important;
          color: rgba(0, 0, 0, 0.6) !important;
          background-color: rgba(255, 255, 255, 0.8) !important;
        }
        .mapboxgl-ctrl-group button:hover {
          background-color: rgba(240, 240, 240, 0.9) !important;
          color: rgba(0, 0, 0, 0.8) !important;
        }
        .mapboxgl-ctrl-group button:active {
          background-color: rgba(220, 220, 220, 0.9) !important;
        }
        
        /* Smooth scrolling like macOS */
        .mapboxgl-canvas-container {
          scroll-behavior: smooth !important;
        }
        
        /* Custom scroll behavior for map */
        .mapboxgl-map {
          scroll-behavior: smooth !important;
        }
      `;
      document.head.appendChild(style);
      console.log('🎛️ Map controls styles added');
    }
  } catch (error) {
    console.error('❌ Error loading Mapbox CSS:', error);
  }
};

import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPopup } from './MapPopup';
import { TaskRisksWidget } from './TaskRisksWidget';
import { getRiskTailwindClasses } from '../styles/designSystem';

const VIEWPORT = {
  latitude: 39.8283,  // Center of USA
  longitude: -98.5795,  // Center of USA
  zoom: 3.5,  // Zoom level to show entire USA width
  maxZoom: 18,  // Increased max zoom for better detail
  minZoom: 2,
  bearing: 0,  // Map rotation
  pitch: 0,    // Map tilt
};

// Function to extract city from site - prioritize Google Address data
const getCityFromSite = (site: any): string => {
  if (!site) return '';
  
  // First, try to use structured Google Address data with new fields
  if (site.google_address_data) {
    const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
    
    // Use locality (city name) from Google Address Data
    if (locality && administrative_area_level_1) {
      return `${locality}, ${administrative_area_level_1}`;
    }
    if (locality) {
      return locality;
    }
    
    // Fallback to old fields for backward compatibility
    if (city && state) {
      return `${city}, ${state}`;
    }
    if (city) {
      return city;
    }
  }
  
  // Fallback to parsing the address string (for backward compatibility)
  const address = site.address;
  if (!address) return '';
  
  // Split address by comma and trim whitespace
  const parts = address.split(',').map((part: string) => part.trim());
  
  // Look for the city part (usually the second part, but not always)
  if (parts.length >= 2) {
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      // Skip if it looks like a state code (2 letters) or zip code (numbers)
      if (!/^\d+$/.test(part) && !/^[A-Z]{2}$/.test(part) && part.length > 2) {
        // Additional check: if this part contains a zip code, extract just the city
        const cityPart = part.split(' ')[0]; // Take first word if it contains zip
        if (cityPart && cityPart.length > 2) {
          return cityPart;
        }
        return part;
      }
    }
    // Fallback to second part if no clear city found
    return parts[1];
  }
  
  // If only one part, return it (might be just city name)
  return parts[0];
};

// Function to get project's highest risk level from project plan items
const getProjectRiskLevel = (project: any): string => {
  if (!project.items || project.items.length === 0) {
    console.log(`🔍 Project "${project.name}": no items, returning 'none'`);
    return 'none';
  }
  
  const riskLevels = project.items.map((item: any) => item.risks || 'none');
  const highestRisk = getHighestRiskLevel(riskLevels);
  const result = highestRisk.toLowerCase();
  
  console.log(`🔍 Project "${project.name}": items=${project.items.length}, riskLevels=${JSON.stringify(riskLevels)}, highestRisk=${highestRisk}, result=${result}`);
  
  return result;
};

// Function to determine project color based on risk level using design system
const getProjectColor = (project: any): string => {
  const riskLevel = getProjectRiskLevel(project);
  const riskClasses = getRiskTailwindClasses(riskLevel);
  return riskClasses.bg;
};

// Function to get adaptive thresholds based on zoom level
const getAdaptiveThresholds = (zoom: number) => {
  // Adaptive thresholds based on zoom level
  if (zoom <= 4) {
    // Very zoomed out - large distances needed
    return {
      overlapThreshold: 0.5,  // 50km threshold
      spreadRadius: 0.15      // 15km spread radius
    };
  } else if (zoom <= 6) {
    // Zoomed out - medium distances
    return {
      overlapThreshold: 0.2,  // 20km threshold
      spreadRadius: 0.08      // 8km spread radius
    };
  } else if (zoom <= 8) {
    // Medium zoom - smaller distances
    return {
      overlapThreshold: 0.1,  // 10km threshold
      spreadRadius: 0.04      // 4km spread radius
    };
  } else if (zoom <= 10) {
    // More zoomed in - even smaller distances
    return {
      overlapThreshold: 0.05, // 5km threshold
      spreadRadius: 0.02      // 2km spread radius
    };
  } else {
    // Very zoomed in - small distances
    return {
      overlapThreshold: 0.01, // 1km threshold
      spreadRadius: 0.008     // 0.8km spread radius
    };
  }
};

// Function to spread overlapping pins with adaptive zoom-based spacing
const spreadOverlappingPins = (servers: any[], zoom: number = 3.5) => {
  const { overlapThreshold, spreadRadius } = getAdaptiveThresholds(zoom);
  
  // Filter servers with valid locations and IDs
  const validServers = servers.filter(server => server.sites?.[0]?.geo_location && server.id);
  
  // Group servers by proximity
  const groups: any[][] = [];
  const processed = new Set<string>();
  
  validServers.forEach(server => {
    if (!server.id || processed.has(server.id)) return;
    
    const [lat, lng] = server.sites![0].geo_location;
    const group = [server];
    processed.add(server.id);
    
    // Find other servers within the overlap threshold
    validServers.forEach(otherServer => {
      if (!otherServer.id || processed.has(otherServer.id)) return;
      
      const [otherLat, otherLng] = otherServer.sites![0].geo_location;
      const distance = Math.sqrt(
        Math.pow(lat - otherLat, 2) + Math.pow(lng - otherLng, 2)
      );
      
      if (distance < overlapThreshold) {
        group.push(otherServer);
        processed.add(otherServer.id);
      }
    });
    
    groups.push(group);
  });
  
  // Spread pins in each group
  const spreadServers = groups.flatMap(group => {
    if (group.length === 1) {
      return group; // No need to spread single pins
    }
    
    // Calculate center point of the group
    const centerLat = group.reduce((sum, s) => sum + s.sites![0].geo_location[0], 0) / group.length;
    const centerLng = group.reduce((sum, s) => sum + s.sites![0].geo_location[1], 0) / group.length;
    
    // Spread pins around the center in a circle
    return group.map((server, index) => {
      const angle = (2 * Math.PI * index) / group.length;
      const offsetLat = spreadRadius * Math.cos(angle);
      const offsetLng = spreadRadius * Math.sin(angle);
      
      return {
        ...server,
        spreadCoordinates: {
          latitude: centerLat + offsetLat,
          longitude: centerLng + offsetLng,
          originalLatitude: server.sites![0].geo_location[0],
          originalLongitude: server.sites![0].geo_location[1],
          isSpread: true
        }
      };
    });
  });
  
  return spreadServers;
};

export const MapPage = ({ showMenu: _showMenu, servers, riskFilter }: {showMenu?: boolean, servers: any[], riskFilter?: string}) => {
  const [selected, setSelected] = useState<string|undefined>(undefined);
  const [currentZoom, setCurrentZoom] = useState<number>(VIEWPORT.zoom);
  const mapRef = useRef<any>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  
  // Load Mapbox CSS when component mounts
  React.useEffect(() => {
    loadMapboxCSS();
    setIsMapLoaded(true);
  }, []);
  
  // Handle zoom changes
  const handleZoomChange = useCallback((evt: any) => {
    const zoom = evt.viewState.zoom;
    setCurrentZoom(zoom);
  }, []);

  // Handle map load
  const handleMapLoad = useCallback(() => {
    console.log('🗺️ Map loaded successfully');
    console.log('🎛️ Navigation controls should be visible');
  }, []);

  // Handle map error
  const handleMapError = useCallback((error: any) => {
    console.error('🗺️ Map error:', error);
  }, []);


  
  // Filter servers based on risk filter
  const filteredServers = useMemo(() => {
    if (!riskFilter || riskFilter === 'all') {
      return servers;
    }
    
    console.log('🔍 Filtering servers with riskFilter:', riskFilter);
    console.log('🔍 Total servers:', servers.length);
    
    const filtered = servers.filter(server => {
      if (!server.items || server.items.length === 0) {
        return false;
      }
      
      // Check if project contains tasks with the specified risk level
      const normalizedFilter = normalizeRiskLevel(riskFilter).toLowerCase();
      const hasMatchingRisk = server.items.some((item: any) => {
        const itemRisk = normalizeRiskLevel(item.risks).toLowerCase();
        return itemRisk === normalizedFilter;
      });
      
      console.log(`🔍 Project "${server.name}": hasMatchingRisk="${hasMatchingRisk}", filter="${normalizedFilter}"`);
      
      return hasMatchingRisk;
    });
    
    console.log('🔍 Filtered servers count:', filtered.length);
    return filtered;
  }, [servers, riskFilter]);
  
  // Process servers to spread overlapping pins with current zoom
  const spreadServers = spreadOverlappingPins(filteredServers, currentZoom);
  
  return (
    <>
      {selected && (
        <MapPopup 
          projectData={servers.find((server) => server.id === selected)!}
          onClose={() => setSelected(undefined)}
        />
      )}
      {isMapLoaded && (
        <MapboxErrorBoundary>
          <Suspense fallback={
            <div className="w-full h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading map...</p>
              </div>
            </div>
          }>
          <Map
            ref={mapRef}
            initialViewState={VIEWPORT}
            style={{width: '100%', height: '100vh'}}
            mapboxAccessToken='pk.eyJ1IjoiZWlnaHR5ZGF5cyIsImEiOiJjam9zYXBiZGswb2NtM2pwdHY5eGZmcGR5In0.lJpch0oIthk_eye7jJnq5A'
            mapStyle="mapbox://styles/eightydays/cjosb2zt801sy2ro3pozm9nn6"
            onMove={handleZoomChange}
            onLoad={handleMapLoad}
            onError={handleMapError}
          >
            {/* Navigation Controls - Zoom in/out, compass */}
            <NavigationControl 
              position="bottom-right"
              showCompass={false}
              showZoom={true}
              visualizePitch={false}
            />
            
            {/* Fullscreen Control */}
            <FullscreenControl position="bottom-right" />
            
            {/* Geolocate Control - Find my location */}
            <GeolocateControl 
              position="bottom-right"
              trackUserLocation={true}
              showUserHeading={true}
              showAccuracyCircle={true}
              onGeolocate={(e) => {
                console.log('📍 User location:', e.coords);
              }}
            />
            

        {spreadServers.map((server) => {
          if (!server.sites?.[0]?.geo_location) return null;

          // Use filter color if filter is active, otherwise use project's risk color
          const projectColor = riskFilter && riskFilter !== 'all' 
            ? getRiskTailwindClasses(riskFilter).bg
            : getProjectColor(server);
          
          const defaultSite = server.sites?.find((site: any) => site.is_default) || server.sites?.[0];
          
          // Validate coordinates before using them
          if (!defaultSite?.geo_location || !Array.isArray(defaultSite.geo_location) || defaultSite.geo_location.length < 2) {
            console.warn('Invalid coordinates for server:', server.name, defaultSite?.geo_location);
            return null;
          }
          
          const [defaultLat, defaultLng] = defaultSite.geo_location;
          
          // Check if coordinates are valid numbers
          if (typeof defaultLat !== 'number' || typeof defaultLng !== 'number' || 
              isNaN(defaultLat) || isNaN(defaultLng)) {
            console.warn('Invalid coordinate values for server:', server.name, { defaultLat, defaultLng });
            return null;
          }
          
          // Use spread coordinates if available, otherwise use original coordinates
          const coordinates = (server as any).spreadCoordinates
            ? {
                latitude: (server as any).spreadCoordinates.latitude,
                longitude: (server as any).spreadCoordinates.longitude
              }
            : {
                latitude: defaultLat,
                longitude: defaultLng
              };
          
          // Final validation of coordinates
          if (typeof coordinates.latitude !== 'number' || typeof coordinates.longitude !== 'number' ||
              isNaN(coordinates.latitude) || isNaN(coordinates.longitude)) {
            console.warn('Final coordinates validation failed for server:', server.name, coordinates);
            return null;
          }

          return (
            <React.Fragment key={server.name}>
              {/* Connection line for spread pins */}
              {(server as any).spreadCoordinates && 
               typeof (server as any).spreadCoordinates.originalLatitude === 'number' &&
               typeof (server as any).spreadCoordinates.originalLongitude === 'number' &&
               !isNaN((server as any).spreadCoordinates.originalLatitude) &&
               !isNaN((server as any).spreadCoordinates.originalLongitude) && (
                <>
                  {/* Original location marker */}
                  <Marker
                    longitude={(server as any).spreadCoordinates.originalLongitude}
                    latitude={(server as any).spreadCoordinates.originalLatitude}
                    anchor="center"
                  >
                    <div className="w-2 h-2 bg-gray-400 rounded-full opacity-60"></div>
                  </Marker>
                  
                  {/* Connection line using Source and Layer */}
                  <Source
                    id={`line-${server.id}`}
                    type="geojson"
                    data={{
                      type: 'Feature',
                      geometry: {
                        type: 'LineString',
                        coordinates: [
                          [(server as any).spreadCoordinates.originalLongitude, (server as any).spreadCoordinates.originalLatitude],
                          [coordinates.longitude, coordinates.latitude]
                        ]
                      },
                      properties: {}
                    }}
                  >
                    <Layer
                      id={`line-layer-${server.id}`}
                      type="line"
                      paint={{
                        'line-color': 'rgba(128, 128, 128, 0.6)',
                        'line-width': 1,
                        'line-dasharray': [2, 2]
                      }}
                    />
                  </Source>
                </>
              )}
              
              {/* Main pin marker */}
              <Marker
                longitude={coordinates.longitude}
                latitude={coordinates.latitude}
                anchor="bottom"
              >
                <Link
                  to={`/projects/${server.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelected(server.id)
                  }}
                  className={
                    cn(
                      "marker bg-white rounded-full shadow-md flex justify-start items-center p-1 gap-1 hover:bg-[#F0F1F4] transition-all"
                    )
                  }
                >
                  <div className={cn('w-10 h-10 rounded-full text-white flex justify-center items-center text-xl', projectColor)}>
                    {riskFilter && riskFilter !== 'all' 
                      ? (server.items?.filter((item: any) => {
                          const itemRisk = normalizeRiskLevel(item.risks).toLowerCase();
                          return itemRisk === riskFilter.toLowerCase();
                        }).length || 0)
                      : (() => {
                          if (server.status === 'NOT_STARTED') return <HiOutlineBadgeCheck size={25} />;
                          if (server.status === 'COMPLETED') return <BsClock size={25} />;
                          if (server.status === 'IN_PROGRESS') return <HiOutlineBadgeCheck size={25} />;
                          return <HiOutlineBadgeCheck size={25} />;
                        })()
                    }
                  </div>
                  <div className='leading-tight pr-3'>
                    <div className='text-base font-medium font-["Arcadia_Text",system-ui,sans-serif]'>{server.name}</div>
                    <div className='text-xs text-gray-500 font-["Arcadia_Text",system-ui,sans-serif]'>{getCityFromSite(defaultSite)}</div>
                  </div>
                </Link>
              </Marker>
            </React.Fragment>
          )}
        )}
          </Map>
        </Suspense>
        </MapboxErrorBoundary>
      )}
      
      {/* Task Risks Widget positioned in bottom left corner */}
      <div className="absolute bottom-4 left-4 z-50">
        <TaskRisksWidget projects={servers} riskFilter={riskFilter} />
      </div>
    </>
  );
}
